import React, { useEffect, useState } from "react";
import "./TransactionHistory.css";
import noRecordsImg from "../../images/shapes/no-record-found 3.png";
import LoginHeader from "../header/Loginheader";
import Footer from "../footer/FooterW";
import { API_URL } from "../../constant";

const TransactionHistory = ({ asModal = false, onClose, filterKeyword = "" }) => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedTransaction, setSelectedTransaction] = useState(null);

  useEffect(() => {
    const fetchTransactions = async () => {
      setLoading(true);
      setError("");

      try {
        const token = localStorage.getItem("auth_token");

        if (!token) {
          throw new Error("User is not authenticated. No token found.");
        }

        const userType = localStorage.getItem("user_type");
        let endpoint = `${API_URL}/api/customer/wallet`;
        if (userType === "retail_business") {
          endpoint = `${API_URL}/api/retail-business/wallet`;
        } else if (userType === "agent") {
          endpoint = `${API_URL}/api/agent/wallet-details`;
        } else if (userType === "member") {
          endpoint = `${API_URL}/api/member/wallet`;
        }

        const response = await fetch(endpoint, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        const contentType = response.headers.get("content-type");
        const isJson = contentType && contentType.includes("application/json");

        if (!response.ok) {
          const errorData = isJson
            ? await response.json()
            : await response.text();
          console.error("❌ API error response:", errorData);

          const message = isJson
            ? errorData.message || "Failed to fetch transactions"
            : `Error ${response.status}: ${response.statusText}`;
          throw new Error(message);
        }

        const result = await response.json();
        console.log("✅ API response:", result);
        
        // Extract transactions from the response based on the endpoint's structure
        let transactionsData = [];
        if (result.transactions) {
          transactionsData = result.transactions;
        } else if (result.data && result.data.recent_transactions) {
          const recent = result.data.recent_transactions;
          transactionsData = [
            ...(recent.primary_wallet || []), 
            ...(recent.retail_agent_wallet || [])
          ];
        } else if (result.data && result.data.transactions) {
          transactionsData = result.data.transactions;
        }
        
        console.log("✅ Transactions fetched:", transactionsData);
        setTransactions(transactionsData);
      } catch (err) {
        console.error("❗ Error fetching transactions:", err.message);
        setError(
          err.message || "An error occurred while fetching transactions."
        );
      } finally {
        setLoading(false);
      }
    };

    fetchTransactions();
  }, []);

  // Format date to a more readable format
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    
    try {
      const options = { year: "numeric", month: "short", day: "numeric" };
      return new Date(dateString).toLocaleDateString(undefined, options);
    } catch (error) {
      console.error("Error formatting date:", error);
      return dateString;
    }
  };

  const filteredTransactions = filterKeyword && filterKeyword.length > 0
    ? transactions.filter(txn => {
        const keywords = Array.isArray(filterKeyword) ? filterKeyword : [filterKeyword];
        const desc = (txn.description || "").toLowerCase();
        const type = (txn.transaction_type || "").toLowerCase();
        
        return keywords.some(kw => 
          kw && (desc.includes(kw.toLowerCase()) || type.includes(kw.toLowerCase()))
        );
      })
    : transactions;

  const renderContent = () => (
    <div className="transaction-content">
      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading transactions...</p>
        </div>
      ) : error ? (
        <div className="error-container">
          <div className="error-icon">⚠️</div>
          <p className="error-message">{error}</p>
          <button
            className="retry-button"
            onClick={() => window.location.reload()}
          >
            Try Again
          </button>
        </div>
      ) : filteredTransactions.length > 0 ? (
        <div className="table-container">
          <div className="table-header">
            <div className="cell date">Date</div>
            <div className="cell type">Type</div>
            <div className="cell description">Description</div>
            <div className="cell units">Amount</div>
          </div>
          <div className="table-body">
            {filteredTransactions.map((txn, idx) => (
              <div 
                className="table-row" 
                key={idx} 
                onClick={() => setSelectedTransaction(txn)}
                style={{ cursor: "pointer" }}
              >
                <div className="cell date" data-label="Date">
                  {formatDate(txn.date || txn.created_at)}
                </div>
                <div className="cell type" data-label="Type">
                  <span
                    className={`type-badge ${(txn.transaction_type || '').toLowerCase()}`}
                  >
                    {txn.transaction_type || 'N/A'}
                  </span>
                </div>
                <div className="cell description" data-label="Description">
                  {txn.description || 'No description'}
                </div>

                <div
                  className={`cell units ${
                    (txn.transaction_type || '').toLowerCase() === 'debit' || 
                    (txn.amount && String(txn.amount).startsWith("-"))
                      ? "negative"
                      : "positive"
                  }`}
                  data-label="Amount"
                >
                  {(txn.transaction_type || '').toLowerCase() === 'debit' || 
                   (txn.amount && String(txn.amount).startsWith("-"))
                    ? "-" 
                    : "+"}
                  {Math.abs(txn.amount || 0)} TopCoins
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="no-records">
          <img src={noRecordsImg} alt="No records" />
          <h3>No transactions yet</h3>
          <p>
            Please make a purchase using your QR code at partner shops to
            earn TopCoin.
          </p>
        </div>
      )}

      {selectedTransaction && (
        <div className="transaction-detail-overlay" onClick={() => setSelectedTransaction(null)}>
          <div className="transaction-detail-modal" onClick={(e) => e.stopPropagation()}>
            <div className="transaction-detail-header">
              <h3>Transaction Details</h3>
              <button className="close-detail-btn" onClick={() => setSelectedTransaction(null)}>&times;</button>
            </div>
            <div className="transaction-detail-content">
              <div className="th-detail-row">
                <span className="th-detail-label">Date</span>
                <span className="th-detail-value">{formatDate(selectedTransaction.date || selectedTransaction.created_at)}</span>
              </div>
              <div className="th-detail-row">
                <span className="th-detail-label">Type</span>
                <span className="th-detail-value" style={{ textTransform: 'capitalize' }}>
                  {selectedTransaction.transaction_type || 'N/A'}
                </span>
              </div>
              <div className="th-detail-row">
                <span className="th-detail-label">Amount</span>
                <span className={`th-detail-value ${
                    (selectedTransaction.transaction_type || '').toLowerCase() === 'debit' || 
                    (selectedTransaction.amount && String(selectedTransaction.amount).startsWith("-"))
                      ? "negative-amount"
                      : "positive-amount"
                  }`}>
                  {(selectedTransaction.transaction_type || '').toLowerCase() === 'debit' || 
                   (selectedTransaction.amount && String(selectedTransaction.amount).startsWith("-"))
                    ? "-" 
                    : "+"}
                  {Math.abs(selectedTransaction.amount || 0)} TopCoins
                </span>
              </div>
              <div className="th-detail-row">
                <span className="th-detail-label">Description</span>
                <span className="th-detail-value description-value">{selectedTransaction.description || 'No description'}</span>
              </div>
              
              {/* Optional extended fields if they exist */}
              {selectedTransaction.status && (
                <div className="th-detail-row">
                  <span className="th-detail-label">Status</span>
                  <span className="th-detail-value" style={{ textTransform: 'capitalize' }}>{selectedTransaction.status}</span>
                </div>
              )}
              {selectedTransaction.reference_id && (
                <div className="th-detail-row">
                  <span className="th-detail-label">Reference ID</span>
                  <span className="th-detail-value">{selectedTransaction.reference_id}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const containerContent = (
    <>
      <div className="transaction-header">
        <h2 className="title">Transaction History</h2>
        <div className="header-right" style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <div className="transaction-count">
            {filteredTransactions.length}{" "}
            {filteredTransactions.length === 1 ? "Transaction" : "Transactions"}
          </div>
          {asModal && (
            <button className="close-modal-btn-txn" onClick={onClose}>
              &times;
            </button>
          )}
        </div>
      </div>
      
      {renderContent()}

      {!asModal && (
        <div className="back-link">
          <a href="/customer/wallet">Back to Wallet &lt;&lt;</a>
        </div>
      )}
    </>
  );

  if (asModal) {
    return (
      <div className="transaction-modal-overlay" onClick={onClose}>
        <div className="transaction-modal-container" onClick={(e) => e.stopPropagation()}>
          {containerContent}
        </div>
      </div>
    );
  }

  return (
    <>
      <LoginHeader />
      <div className="transaction-container">
        {containerContent}
      </div>
      <Footer />
    </>
  );
};

export default TransactionHistory;